import React, { useEffect, useState } from 'react';
import socketIOClient from 'socket.io-client';
import { useDispatch, useSelector } from 'react-redux';
import {
  UserCircleIcon,
  ChatBubbleLeftEllipsisIcon,
  NewspaperIcon,
  UserPlusIcon,
  ShareIcon
} from '@heroicons/react/20/solid';
import { server } from '../server';
import { socketEndpoint } from '../config/socket';
import { Link, useNavigate } from 'react-router-dom';
import PropTypes from 'prop-types';
import { loadNotifications } from '../redux/actions/notifications';
import { getStoredAuthToken } from '../utils/authSession';
import { getStoredCurrentProfile } from '../utils/shopProfile';

const socket = socketIOClient(socketEndpoint, { withCredentials: true });

function NotificationFeed({ profileId, profileType, onClose, onOpenMessages }) {
  const [roomInviteActionId, setRoomInviteActionId] = useState('');
  const [roomInviteError, setRoomInviteError] = useState(null);
  const dispatch = useDispatch();
  const notifications = useSelector((state) => (
    Array.isArray(state.notifications.notifications) ? state.notifications.notifications : []
  ));
  const navigate = useNavigate();

  const getIconForType = (type) => {
    switch (type) {
      case 'newComment':
      case 'newMessage':
        return <ChatBubbleLeftEllipsisIcon className="notification-feed-icon" aria-hidden="true" />;
      case 'newLike':
        return (
          <img
            src="https://res.cloudinary.com/dzlopmfj8/image/upload/v1724845531/icon-BuzzBolt-02_ernlpk.png"
            alt="Like Notification"
            className="notification-feed-icon"
          />
        );
      case 'newPost':
        return <NewspaperIcon className="notification-feed-icon" aria-hidden="true" />;
      case 'follow':
      case 'roomInvite':
        return <UserPlusIcon className="notification-feed-icon" aria-hidden="true" />;
      case 'reshare':
        return <ShareIcon className="notification-feed-icon" aria-hidden="true" />;
      default:
        return null;
    }
  };

  useEffect(() => {
    if (!profileId) {
      return undefined;
    }

    dispatch(loadNotifications(profileType, profileId));

    const handleNotification = (newNotification) => {
      const recipientId = newNotification?.recipient?._id || newNotification?.recipient;

      if (String(recipientId || '') !== String(profileId || '') || newNotification?.recipientProfileType !== profileType) {
        return;
      }

      dispatch(loadNotifications(profileType, profileId));
    };

    socket.on('notification', handleNotification);

    return () => socket.off('notification', handleNotification);
  }, [dispatch, profileId, profileType]);

  const getProfileLink = (senderProfileType, sender) => {
    if (!sender?.handle) {
      return '#';
    }

    if (senderProfileType === 'User') {
      return `/user/${sender.handle}`;
    } else if (senderProfileType === 'Shop') {
      return `/shop/${sender.handle}`;
    }
    return '#';
  };

  const getAvatarSrc = (profile) => {
    if (!profile?.avatar) {
      return '';
    }

    return typeof profile.avatar === 'string' ? profile.avatar : profile.avatar.url || '';
  };

  const getRoomLink = (room) => {
    if (!room?.handle) {
      return '#';
    }

    return `/rooms/${room.handle}`;
  };

  const handleRoomInviteAction = async (notification, action) => {
    const roomId = notification.relatedRoom?._id;

    if (!roomId || roomInviteActionId) {
      return;
    }

    const token = getStoredAuthToken();
    const headers = {
      'Content-Type': 'application/json',
      'current-profile': profileType === 'User' ? 'User' : getStoredCurrentProfile() || profileType,
    };

    if (token) {
      headers.Authorization = `Bearer ${token}`;
    }

    setRoomInviteError(null);
    setRoomInviteActionId(`${notification._id}:${action}`);

    try {
      const response = await fetch(`${server}/room/rooms/${roomId}/member-invite`, {
        method: 'PATCH',
        credentials: 'include',
        headers,
        body: JSON.stringify({ action }),
      });
      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Could not update invitation.');
      }

      dispatch(loadNotifications(profileType, profileId));
    } catch (error) {
      setRoomInviteError({
        id: notification._id,
        message: error.message || 'Could not update invitation.',
      });
    } finally {
      setRoomInviteActionId('');
    }
  };

  const handleOpenRelatedPost = (post) => {
    if (!post?._id) {
      return;
    }

    onClose?.();
    navigate(`/post/${post._id}`);
  };

  const handleOpenMessages = () => {
    onOpenMessages?.();
  };

  return (
    <div className="notification-feed-flow-root">
      {(!notifications || notifications.length === 0) ? (
        <div className="notification-feed-empty">
          <p className="notification-feed-empty-message">You don't have any recent notifications</p>
        </div>
      ) : (
        <ul role="list" className="notification-feed-list">
          {notifications.map((notification, idx) => (
            <li key={notification._id}>
              <div className="notification-feed-item">
                {idx !== notifications.length - 1 && (
                  <span className="notification-feed-connector" aria-hidden="true" />
                )}
                <div className="notification-feed-content-wrapper">
                  <div className="notification-feed-icon-wrapper">
                    {getIconForType(notification.type)}
                  </div>
                  <div className="notification-feed-text-wrapper">
                    <div className="notification-feed-primary-row">
                      {notification.sender && (
                        <div className="notification-feed-avatar-wrapper">
                          {getAvatarSrc(notification.sender) ? (
                            <img
                              className="notification-feed-avatar"
                              src={getAvatarSrc(notification.sender)}
                              alt="Sender avatar"
                            />
                          ) : <UserCircleIcon className="notification-feed-avatar" aria-hidden="true" />}
                        </div>
                      )}
                      <div className="notification-feed-text">
                        <p>
                          <Link to={getProfileLink(notification.senderProfileType, notification.sender)} className="notification-feed-link">
                            <strong>{notification.sender?.name || 'MATRIX'}</strong>
                          </Link>
                        </p>
                        <p>{notification.message}</p>
                        <p>{new Date(notification.createdAt).toLocaleString()}</p>
                      </div>
                    </div>

                    {notification.type === 'roomInvite' && notification.relatedRoom && (
                      <div className="notification-feed-actions notification-feed-room-invite-actions">
                        <Link to={getRoomLink(notification.relatedRoom)} className="notification-feed-secondary-action" onClick={onClose}>
                          View Community
                        </Link>
                        {notification.roomInviteStatus ? (
                          <span className="notification-feed-action-status">
                            {notification.roomInviteStatus === 'accepted' ? 'Accepted' : 'Declined'}
                          </span>
                        ) : (
                          <>
                            <button
                              type="button"
                              className="notification-feed-primary-action"
                              onClick={() => handleRoomInviteAction(notification, 'accept')}
                              disabled={Boolean(roomInviteActionId)}
                            >
                              {roomInviteActionId === `${notification._id}:accept` ? 'Accepting...' : 'Accept'}
                            </button>
                            <button
                              type="button"
                              className="notification-feed-secondary-action"
                              onClick={() => handleRoomInviteAction(notification, 'decline')}
                              disabled={Boolean(roomInviteActionId)}
                            >
                              {roomInviteActionId === `${notification._id}:decline` ? 'Declining...' : 'Decline'}
                            </button>
                          </>
                        )}
                      </div>
                    )}

                    {roomInviteError?.id === notification._id && (
                      <p className="notification-feed-action-error">{roomInviteError.message}</p>
                    )}

                    {notification.relatedPost && (
                      <button
                        className="notifcations-view-post-button"
                        onClick={() => handleOpenRelatedPost(notification.relatedPost)}
                      >
                        View Post
                      </button>
                    )}

                    {notification.type === 'newMessage' && (
                      <button
                        type="button"
                        className="notification-feed-primary-action notification-feed-open-messages-button"
                        onClick={handleOpenMessages}
                      >
                        Open Messages
                      </button>
                    )}
                  </div>
                </div>
              </div>
            </li>
          ))}
        </ul>
      )}

    </div>
  );
}

// PropTypes for better type safety and documentation
NotificationFeed.propTypes = {
  profileId: PropTypes.string.isRequired,
  profileType: PropTypes.oneOf(['User', 'Shop']).isRequired,
  onClose: PropTypes.func,
  onOpenMessages: PropTypes.func,
};

export default NotificationFeed;











